/**
 * IdentityConfigTab Component
 * Configure agent identity: display name, role, personality traits, tone, gender, avatar
 */

import React, { useState, useEffect } from 'react';
import { Card, Form, Badge, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  IconUser,
  IconId,
  IconMoodSmile,
  IconVolume,
  IconGenderBigender,
  IconPhoto,
  IconInfoCircle,
  IconX,
  IconPlus,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { PERSONALITY_TRAITS, TONE_OPTIONS, GENDER_OPTIONS } from '../../constants';
import './IdentityConfigTab.css';

// ===============================================
// PERSONALITY TRAIT SELECTOR
// ===============================================

const PersonalityTraitSelector = ({ selected, onChange }) => {
  const toggleTrait = (traitId) => {
    if (selected.includes(traitId)) {
      onChange(selected.filter(t => t !== traitId));
    } else if (selected.length < 3) {
      onChange([...selected, traitId]);
    }
  };

  return (
    <div className="personality-traits-grid">
      {PERSONALITY_TRAITS.map((trait) => {
        const isSelected = selected.includes(trait.id);
        return (
          <div
            key={trait.id}
            className={`personality-trait-card ${isSelected ? 'selected' : ''}`}
            onClick={() => toggleTrait(trait.id)}
          >
            <div className="trait-content">
              <span className="trait-name">{trait.name}</span>
              <span className="trait-description">{trait.description}</span>
            </div>
            {isSelected && (
              <Badge bg="primary" className="trait-badge">
                <IconX size={12} />
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const IdentityConfigTab = ({ agentId }) => {
  const { state, updateIdentityConfig } = useAgentsFlow();
  const { editor } = state;

  const [config, setConfig] = useState({
    display_name: '',
    role: '',
    personality_traits: [],
    tone: 'professional',
    gender: 'neutral',
    avatar_url: '',
  });

  // Load existing config
  useEffect(() => {
    if (editor?.identity_config) {
      setConfig({
        display_name: editor.identity_config.display_name || '',
        role: editor.identity_config.role || '',
        personality_traits: (editor.identity_config.personality_traits || []).slice(0, 3),
        tone: editor.identity_config.tone || 'professional',
        gender: editor.identity_config.gender || 'neutral',
        avatar_url: editor.identity_config.avatar_url || '',
      });
    }
  }, [editor?.identity_config]);

  // Handle change
  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    updateIdentityConfig(newConfig);
  };

  return (
    <div className="tab-content-wrapper">
      {/* Basic Identity */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconId size={20} />
            Identidade Básica
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="d-flex align-items-center gap-2">
                  Nome de Exibição
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>O nome que o agente usará para se apresentar aos clientes</Tooltip>}
                  >
                    <IconInfoCircle size={14} className="text-muted" style={{ cursor: 'help' }} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Ana, Dr. Carlos, Assistente Virtual"
                  value={config.display_name}
                  onChange={(e) => handleChange('display_name', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Como o agente se apresentará nas conversas
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="d-flex align-items-center gap-2">
                  Função / Cargo
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>A função que o agente desempenhará no atendimento</Tooltip>}
                  >
                    <IconInfoCircle size={14} className="text-muted" style={{ cursor: 'help' }} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Assistente Virtual, Atendente, Recepcionista"
                  value={config.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                />
                <Form.Text className="text-muted">
                  A função ou cargo do agente
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>URL do Avatar (opcional)</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://exemplo.com/avatar.png"
                  value={config.avatar_url}
                  onChange={(e) => handleChange('avatar_url', e.target.value)}
                />
                <Form.Text className="text-muted">
                  URL da imagem do avatar do agente
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              {config.avatar_url && (
                <div className="avatar-preview">
                  <img
                    src={config.avatar_url}
                    alt="Avatar preview"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tone & Gender */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconVolume size={20} />
            Tom e Comunicação
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tom de Comunicação</Form.Label>
                <div className="tone-options">
                  {TONE_OPTIONS.map((tone) => (
                    <div
                      key={tone.id}
                      className={`tone-option ${config.tone === tone.id ? 'selected' : ''}`}
                      onClick={() => handleChange('tone', tone.id)}
                    >
                      <strong>{tone.name}</strong>
                      <span>{tone.description}</span>
                    </div>
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Gênero da Identidade</Form.Label>
                <div className="gender-options">
                  {GENDER_OPTIONS.map((gender) => (
                    <div
                      key={gender.id}
                      className={`gender-option ${config.gender === gender.id ? 'selected' : ''}`}
                      onClick={() => handleChange('gender', gender.id)}
                    >
                      <strong>{gender.name}</strong>
                      <span>{gender.description}</span>
                    </div>
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Personality Traits */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconMoodSmile size={20} />
            Traços de Personalidade
            <Badge bg="secondary" className="ms-2">
              {config.personality_traits.length}/3
            </Badge>
          </h6>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Selecione até 3 traços que definem a personalidade do seu agente. Isso influenciará
            como ele se comunica e interage com os clientes.
          </p>
          <PersonalityTraitSelector
            selected={config.personality_traits}
            onChange={(traits) => handleChange('personality_traits', traits)}
          />
        </Card.Body>
      </Card>

      {/* Preview */}
      <Card>
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconUser size={20} />
            Preview da Identidade
          </h6>
        </Card.Header>
        <Card.Body>
          <div className="identity-preview">
            <div className="preview-avatar">
              {config.avatar_url ? (
                <img src={config.avatar_url} alt="Avatar" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <IconUser size={32} />
              )}
            </div>
            <div className="preview-info">
              <h5>{config.display_name || 'Nome do Agente'}</h5>
              <p className="text-muted mb-2">{config.role || 'Função do Agente'}</p>
              <div className="preview-traits">
                {config.personality_traits.map((traitId) => {
                  const trait = PERSONALITY_TRAITS.find(t => t.id === traitId);
                  return trait ? (
                    <Badge key={traitId} bg="light" text="dark" className="me-1">
                      {trait.name}
                    </Badge>
                  ) : null;
                })}
              </div>
              <div className="preview-meta mt-2">
                <Badge bg="info" className="me-2">
                  {TONE_OPTIONS.find(t => t.id === config.tone)?.name || 'Profissional'}
                </Badge>
                <Badge bg="secondary">
                  {GENDER_OPTIONS.find(g => g.id === config.gender)?.name || 'Neutro'}
                </Badge>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default IdentityConfigTab;
